// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { authApi, type ShareLinkPatch } from '../../api/auth';
import type { ShareLink, ShareLinkCreated } from '../../api/types';
import { useArmedConfirm } from '../../app/useArmedConfirm';
import { useAsync } from '../../app/useAsync';
import { useMutationQueue } from '../../app/useMutationQueue';
import {
  activeCountText,
  canEditLink,
  canManageShareLinks,
  type Caller,
  createdLinkUrl,
  creatorText,
  dateInputBounds,
  EDIT_EXPIRY_CHOICES,
  editExpiryLabel,
  editLinkDraft,
  type EditLinkDraft,
  editLinkPatch,
  endsSessions,
  EXPIRY_PRESET_LABELS,
  EXPIRY_PRESETS,
  type EditExpiry,
  type ExpiryPreset,
  formatDate,
  hasChanges,
  LABEL_MAX,
  lastUsedText,
  type LinkAction,
  LINK_STATE_LABELS,
  LINK_STATE_TONES,
  linkLabel,
  linkValidityText,
  newLinkBody,
  PASSWORD_MAX,
  PASSWORD_SESSIONS_NOTE,
  type PasswordChange,
  savedLinkText,
  SHARE_LINKS_DENIED_TEXT,
  shareLinkErrorText,
  sortShareLinks,
} from '../../deling/model';
import { Button } from '../Button';
import { DataTable, type Column } from '../DataTable';
import { Pill } from '../Pill';
import { Select } from '../Select';
import { Spinner } from '../Spinner';
import { TextInput } from '../TextInput';
import { TextLink } from '../TextLink';
import controls from '../controls.module.css';
import styles from './Deling.module.css';

/**
 * Delingslinks — read-only links to the case for people without an account
 * (ruxd-wmc.4). They belong to the case, not to whoever made them: its owners
 * and an admin list, make, edit and revoke them; everyone else gets a note
 * and no request. The token is shown once, as a full URL to copy. Revoked
 * links stay listed, and can no longer be edited.
 */
export function ShareLinksPanel({ cid, caller }: { cid: string; caller: Caller }) {
  const headingId = useId();
  if (!canManageShareLinks(caller))
    return (
      <section className={styles.panel} aria-labelledby={headingId}>
        <h2 id={headingId} className={styles.panelHeading}>
          Delingslinks
        </h2>
        <p className={styles.muted}>{SHARE_LINKS_DENIED_TEXT}</p>
      </section>
    );
  return <ShareLinks cid={cid} headingId={headingId} />;
}

function ShareLinks({ cid, headingId }: { cid: string; headingId: string }) {
  const links = useAsync<ShareLink[]>((s) => authApi.shareLinks(cid, s), [cid]);
  const [label, setLabel] = useState('');
  const [password, setPassword] = useState('');
  const [preset, setPreset] = useState<ExpiryPreset>('30d');
  const [date, setDate] = useState('');
  const [created, setCreated] = useState<ShareLinkCreated | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const action = useRef<LinkAction>('create');
  const bounds = dateInputBounds(new Date());

  const { busy, mutate } = useMutationQueue({
    scope: 'page',
    onError: (cause) => setFailure(shareLinkErrorText(cause, action.current)),
    onSettled: () => links.reload(),
  });
  const confirm = useArmedConfirm<number>(busy, links.data);

  const create = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const result = newLinkBody({ label, password, preset, date }, new Date());
    if ('error' in result) {
      setFailure(result.error);
      return;
    }
    setFailure(null);
    setCreated(null);
    setSaved(null);
    action.current = 'create';
    void mutate(async () => {
      setCreated(await authApi.createShareLink(cid, result.body));
      setLabel('');
      setPassword('');
    });
  };

  const revoke = (l: ShareLink) => {
    setFailure(null);
    setSaved(null);
    confirm.disarm();
    action.current = 'revoke';
    void mutate(async () => {
      await authApi.revokeShareLink(cid, l.id);
      setCreated((c) => (c?.id === l.id ? null : c));
      setEditingId((id) => (id === l.id ? null : id));
    });
  };

  const startEdit = (l: ShareLink) => {
    setFailure(null);
    setSaved(null);
    setEditingId(l.id);
  };

  const now = new Date();
  const list = links.data ? sortShareLinks(links.data) : [];
  // The link under edit, while it still can be: a reload that shows it revoked closes the form.
  const editing = list.find((l) => l.id === editingId && canEditLink(l)) ?? null;

  const columns: Column<ShareLink>[] = [
    {
      key: 'label',
      header: 'Navn',
      render: (l) => (
        <span className={`${styles.name} ${l.state === 'active' ? '' : styles.inactive}`}>
          <span className={styles.nameLine}>
            {linkLabel(l.label)}
            {l.has_password && (
              <Pill variant="outline" title="Linket kræver en adgangskode">
                Adgangskode
              </Pill>
            )}
          </span>
          <span className={styles.sub}>
            {creatorText(l)} · {formatDate(l.created_at)}
          </span>
        </span>
      ),
    },
    {
      key: 'state',
      header: 'Status',
      render: (l) => <Pill tone={LINK_STATE_TONES[l.state]}>{LINK_STATE_LABELS[l.state]}</Pill>,
    },
    {
      key: 'validity',
      header: 'Udløb og brug',
      render: (l) => (
        <span className={styles.stack}>
          <span className={styles.meta}>{linkValidityText(l, now)}</span>
          <span className={styles.sub}>{lastUsedText(l.last_used_at)}</span>
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (l) =>
        !canEditLink(l) ? null : (
          <span className={styles.actions}>
            <Button
              variant="outline"
              size="sm"
              disabled={busy || editing?.id === l.id}
              aria-label={`Redigér ${linkLabel(l.label)}`}
              onClick={() => startEdit(l)}
            >
              Redigér
            </Button>
            {l.state !== 'active' ? null : confirm.armed === l.id ? (
              <Button
                variant="outline"
                tone="danger"
                size="sm"
                armed
                disabled={busy}
                onClick={() => revoke(l)}
                onBlur={confirm.disarm}
              >
                Tilbagekald?
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={(e) => confirm.arm(l.id, e.currentTarget)}
              >
                Tilbagekald
              </Button>
            )}
          </span>
        ),
    },
  ];

  return (
    <section className={styles.panel} aria-labelledby={headingId}>
      <div className={styles.panelHead}>
        <h2 id={headingId} className={styles.panelHeading}>
          Delingslinks
        </h2>
        {links.data && <span className={styles.count}>{activeCountText(links.data)}</span>}
      </div>
      <p className={styles.muted}>
        Et link giver læseadgang til sagen uden en konto: modtageren kan se alt, men ikke ændre noget. Linkene hører til
        sagen, ikke til den, der oprettede dem. Giv et link en adgangskode og en udløbsdato, hvis det skal videre end til
        én person.
      </p>

      {created && <CreatedLink link={created} onDismiss={() => setCreated(null)} />}
      {saved && (
        <div className={styles.saved} role="status">
          <span>{saved}</span>
          <TextLink size="sm" onClick={() => setSaved(null)}>
            Luk
          </TextLink>
        </div>
      )}

      {links.error ? (
        <p className={styles.error}>
          {shareLinkErrorText(links.error, 'list')}{' '}
          <TextLink onClick={links.reload}>Prøv igen</TextLink>
        </p>
      ) : !links.data ? (
        <Spinner label="Indlæser links…" />
      ) : (
        <DataTable
          columns={columns}
          rows={list}
          rowKey={(l) => String(l.id)}
          empty={<p className={styles.muted}>Sagen har ingen delingslinks.</p>}
        />
      )}

      {editing && (
        <EditLink
          key={editing.id}
          link={editing}
          busy={busy}
          onCancel={() => setEditingId(null)}
          onInvalid={setFailure}
          onSave={(patch) => {
            setFailure(null);
            setSaved(null);
            action.current = 'update';
            void mutate(async () => {
              await authApi.updateShareLink(cid, editing.id, patch);
              setEditingId(null);
              setSaved(savedLinkText(patch));
            });
          }}
        />
      )}

      <h3 className={styles.formHeading}>Nyt link</h3>
      <form className={styles.row} onSubmit={create}>
        <label className={controls.field}>
          <span className={controls.fieldLabel}>Navn (valgfrit)</span>
          <TextInput
            variant="outline"
            size="sm"
            block
            value={label}
            maxLength={LABEL_MAX}
            placeholder="Fx Bygherre, Nordhavn Ejendomme"
            autoComplete="off"
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>
        <label className={controls.field}>
          <span className={controls.fieldLabel}>Adgangskode (valgfri)</span>
          <TextInput
            variant="outline"
            size="sm"
            block
            type="password"
            value={password}
            maxLength={PASSWORD_MAX}
            placeholder="Mindst 4 tegn"
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <label className={`${controls.field} ${styles.narrow}`}>
          <span className={controls.fieldLabel}>Udløber</span>
          <Select variant="outline" size="sm" value={preset} onChange={(e) => setPreset(e.target.value as ExpiryPreset)}>
            {EXPIRY_PRESETS.map((p) => (
              <option key={p} value={p}>
                {p === 'never' || p === 'date' ? EXPIRY_PRESET_LABELS[p] : `Om ${EXPIRY_PRESET_LABELS[p]}`}
              </option>
            ))}
          </Select>
        </label>
        {preset === 'date' && (
          <label className={`${controls.field} ${styles.narrow}`}>
            <span className={controls.fieldLabel}>Dato</span>
            <TextInput
              variant="outline"
              size="sm"
              block
              type="date"
              value={date}
              min={bounds.min}
              max={bounds.max}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
        )}
        <Button type="submit" variant="primary" size="sm" disabled={busy || (preset === 'date' && !date)}>
          Opret link
        </Button>
      </form>
      {failure && (
        <p className={styles.error} role="alert">
          {failure}
        </p>
      )}
    </section>
  );
}

/** Editing one link: its name, its expiry and its password. Only what changed is sent. */
function EditLink({
  link,
  busy,
  onCancel,
  onInvalid,
  onSave,
}: {
  link: ShareLink;
  busy: boolean;
  onCancel: () => void;
  onInvalid: (error: string) => void;
  onSave: (patch: ShareLinkPatch) => void;
}) {
  const headingId = useId();
  const [draft, setDraft] = useState<EditLinkDraft>(() => editLinkDraft(link));
  const set = (part: Partial<EditLinkDraft>) => setDraft((d) => ({ ...d, ...part }));
  const now = new Date();
  const bounds = dateInputBounds(now);
  const result = editLinkPatch(link, draft, now);
  const unchanged = 'patch' in result && !hasChanges(result.patch);
  const passwordChoices: { value: PasswordChange; label: string }[] = link.has_password
    ? [
        { value: 'keep', label: 'Behold adgangskoden' },
        { value: 'set', label: 'Skift adgangskode' },
        { value: 'remove', label: 'Fjern adgangskoden' },
      ]
    : [
        { value: 'keep', label: 'Ingen adgangskode' },
        { value: 'set', label: 'Sæt adgangskode' },
      ];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if ('error' in result) onInvalid(result.error);
    else if (hasChanges(result.patch)) onSave(result.patch);
  };

  return (
    <form className={styles.editor} aria-labelledby={headingId} onSubmit={submit}>
      <h3 id={headingId} className={styles.formHeading}>
        Redigér “{linkLabel(link.label)}”
      </h3>
      <div className={styles.row}>
        <label className={controls.field}>
          <span className={controls.fieldLabel}>Navn</span>
          <TextInput
            variant="outline"
            size="sm"
            block
            value={draft.label}
            maxLength={LABEL_MAX}
            placeholder="Link uden navn"
            autoComplete="off"
            onChange={(e) => set({ label: e.target.value })}
          />
        </label>
        <label className={`${controls.field} ${styles.narrow}`}>
          <span className={controls.fieldLabel}>Udløber</span>
          <Select
            variant="outline"
            size="sm"
            value={draft.expiry}
            onChange={(e) => set({ expiry: e.target.value as EditExpiry })}
          >
            {EDIT_EXPIRY_CHOICES.map((c) => (
              <option key={c} value={c}>
                {editExpiryLabel(c, link, now)}
              </option>
            ))}
          </Select>
        </label>
        {draft.expiry === 'date' && (
          <label className={`${controls.field} ${styles.narrow}`}>
            <span className={controls.fieldLabel}>Dato</span>
            <TextInput
              variant="outline"
              size="sm"
              block
              type="date"
              value={draft.date}
              min={bounds.min}
              max={bounds.max}
              onChange={(e) => set({ date: e.target.value })}
            />
          </label>
        )}
      </div>
      <div className={styles.row}>
        <label className={`${controls.field} ${styles.narrow}`}>
          <span className={controls.fieldLabel}>Adgangskode</span>
          <Select
            variant="outline"
            size="sm"
            value={draft.passwordChange}
            onChange={(e) => set({ passwordChange: e.target.value as PasswordChange, password: '' })}
          >
            {passwordChoices.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
        </label>
        {draft.passwordChange === 'set' && (
          <label className={controls.field}>
            <span className={controls.fieldLabel}>Ny adgangskode</span>
            <TextInput
              variant="outline"
              size="sm"
              block
              type="password"
              value={draft.password}
              maxLength={PASSWORD_MAX}
              placeholder="Mindst 4 tegn"
              autoComplete="new-password"
              onChange={(e) => set({ password: e.target.value })}
            />
          </label>
        )}
      </div>
      {'patch' in result && endsSessions(result.patch) ? (
        <p className={styles.warning}>{PASSWORD_SESSIONS_NOTE}</p>
      ) : (
        <p className={styles.hint}>{PASSWORD_SESSIONS_NOTE}</p>
      )}
      <div className={styles.editorActions}>
        <Button type="submit" variant="primary" size="sm" disabled={busy || unchanged}>
          Gem ændringer
        </Button>
        <Button variant="outline" size="sm" disabled={busy} onClick={onCancel}>
          Annullér
        </Button>
      </div>
    </form>
  );
}

/** The new link's full URL, once: copy it now or make a new one later. */
function CreatedLink({ link, onDismiss }: { link: ShareLinkCreated; onDismiss: () => void }) {
  const url = createdLinkUrl(window.location.origin, link.token);
  const input = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState<boolean | null>(null);

  useEffect(() => {
    setCopied(null);
    input.current?.focus();
    input.current?.select();
  }, [url]);

  const copy = () => {
    input.current?.select();
    navigator.clipboard?.writeText(url).then(
      () => setCopied(true),
      () => setCopied(false),
    ) ?? setCopied(false);
  };

  return (
    <div className={styles.created} role="status">
      <div className={styles.createdHead}>
        <span className={styles.createdTitle}>Linket “{linkLabel(link.label)}” er oprettet</span>
        <TextLink size="sm" onClick={onDismiss}>
          Luk
        </TextLink>
      </div>
      <div className={styles.urlRow}>
        <TextInput
          ref={input}
          variant="outline"
          size="sm"
          mono
          className={styles.url}
          readOnly
          value={url}
          aria-label="Linkets adresse"
          onFocus={(e) => e.currentTarget.select()}
        />
        <Button variant="primary" size="sm" onClick={copy}>
          {copied ? 'Kopieret' : 'Kopiér'}
        </Button>
      </div>
      <span className={styles.createdNote}>
        {copied === false
          ? 'Kunne ikke kopiere automatisk — markér adressen og kopiér den selv. '
          : ''}
        Adressen vises kun nu. Kopiér den, før du lukker: senere kan den ikke hentes igen, kun tilbagekaldes.
        {link.has_password && ' Send adgangskoden ad en anden vej end linket.'}
      </span>
    </div>
  );
}
